/**
 * Loyalty Context
 * Manages customer loyalty wallet state
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { loyaltyService } from '../services/loyaltyService';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';

const LoyaltyContext = createContext(undefined);

export function LoyaltyProvider({ children }) {
  const [wallet, setWallet] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { isAuthenticated } = useAuth();
  const { showToast } = useToast();

  /**
   * Fetch wallet data from server
   */
  const refreshWallet = useCallback(async () => {
    if (!isAuthenticated) {
      setWallet(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const response = await loyaltyService.getMyWallet();

      if (response.data.success && response.data.data) {
        setWallet(response.data.data);
        console.log('Wallet loaded:', response.data.data);
      } else {
        console.warn('Failed to load wallet:', response.data.message);
        setWallet(null);
      }
    } catch (error) {
      console.error('❌ Error fetching wallet:', error);
      // Don't show toast on initial load failure - wallet might not exist yet
      setWallet(null);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  /**
   * Fetch wallet when authentication state changes
   */
  useEffect(() => {
    if (isAuthenticated) {
      refreshWallet();
    } else {
      // Clear wallet on logout
      setWallet(null);
      setIsLoading(false);
    }
  }, [isAuthenticated, refreshWallet]);

  // The API returns { wallet: {...}, cashValue, redeemRatio, earnRatio, expiringNext }
  const walletDoc = wallet?.wallet || null;
  const cashValue = wallet?.cashValue || 0;
  const earnRatio = wallet?.earnRatio || 0;
  const redeemRatio = wallet?.redeemRatio || 1;
  const expiringNext = wallet?.expiringNext || null;

  const value = {
    // State - expose the wallet document directly
    wallet: walletDoc,
    cashValue,
    earnRatio,
    redeemRatio,
    expiringNext,
    isLoading,

    // Methods
    refreshWallet,
  };

  return (
    <LoyaltyContext.Provider value={value}>
      {children}
    </LoyaltyContext.Provider>
  );
}

/**
 * Custom hook to use loyalty context
 */
export function useLoyalty() {
  const context = useContext(LoyaltyContext);
  if (context === undefined) {
    throw new Error('useLoyalty must be used within a LoyaltyProvider');
  }
  return context;
}

export default LoyaltyContext;
